import { supabase } from '@/lib/supabase';
import type { SecurityEvent, SecurityEventSeverity } from '@/types';

export type SecurityEventsListParams = {
  orgId?: string;
  severity?: SecurityEventSeverity;
  eventType?: string;
  since?: string;
  limit?: number;
};

const SEVERITY_RANK: Record<SecurityEventSeverity, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
  info: 4,
};

function sortSecurityEvents(rows: SecurityEvent[]): SecurityEvent[] {
  return [...rows].sort((a, b) => {
    const rankDiff = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
    if (rankDiff !== 0) return rankDiff;
    return new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime();
  });
}

export const securityEventsApi = {
  list: async (params: SecurityEventsListParams = {}): Promise<SecurityEvent[]> => {
    const { data, error } = await supabase.rpc('admin_list_security_events', {
      p_org_id: params.orgId ?? null,
      p_severity: params.severity ?? null,
      p_type: params.eventType ?? null,
      p_since: params.since ?? null,
      p_limit: params.limit ?? 200,
    });
    if (error) throw error;

    const rows = (data ?? []) as SecurityEvent[];
    if (!rows.length) return [];

    const orgIds = [...new Set(rows.map((row) => row.organization_id).filter(Boolean))] as string[];
    const orgById = new Map<string, { id: string; name: string }>();

    if (orgIds.length) {
      const { data: orgs, error: orgsError } = await supabase
        .from('organizations')
        .select('id, name')
        .in('id', orgIds);
      if (orgsError) throw orgsError;
      (orgs ?? []).forEach((org: { id: string; name: string }) => orgById.set(org.id, org));
    }

    return sortSecurityEvents(
      rows.map((row) => ({
        ...row,
        metadata:
          row.metadata && typeof row.metadata === 'object' && !Array.isArray(row.metadata)
            ? row.metadata
            : {},
        organization: row.organization_id ? orgById.get(row.organization_id) ?? null : null,
      })),
    );
  },
};
